import { types, Instance } from "mobx-state-tree";

const PANEL_HEIGHT_KEY = "playgroundTimelinePanelHeight";
const PANEL_COLLAPSED_KEY = "playgroundTimelinePanelCollapsed";
const CLIP_TO_WORKAREA_KEY = "playgroundTimelineClipToWorkarea";
const LABEL_WIDTH_KEY = "playgroundTimelineLabelWidth";
const MIN_LABEL_WIDTH = 160;
const MAX_LABEL_WIDTH = 520;
const SCROLL_END_PADDING_PX = 40;

const clampLabelWidth = (width: number): number => {
  if (!Number.isFinite(width)) return 200;
  return Math.max(MIN_LABEL_WIDTH, Math.min(MAX_LABEL_WIDTH, width));
};

function getMaxScrollOffset({
  clipToWorkarea,
  contentMaxFrame,
  viewportWidth,
  workAreaFrames,
  zoom,
}: {
  clipToWorkarea: boolean;
  contentMaxFrame: number;
  viewportWidth: number;
  workAreaFrames: number;
  zoom: number;
}): number | null {
  if (viewportWidth <= 0 || zoom <= 0) {
    return null;
  }

  const scrollableFrames = clipToWorkarea
    ? workAreaFrames
    : Math.max(workAreaFrames, contentMaxFrame);
  const scrollableWidth = Math.max(0, scrollableFrames * zoom);

  return Math.max(0, scrollableWidth - viewportWidth + SCROLL_END_PADDING_PX);
}

const getStoredHeight = (): number => {
  try {
    const stored = localStorage.getItem(PANEL_HEIGHT_KEY);
    return stored ? parseInt(stored, 10) : 200;
  } catch {
    return 200;
  }
};

const getStoredCollapsed = (): boolean => {
  try {
    const stored = localStorage.getItem(PANEL_COLLAPSED_KEY);
    return stored === "true";
  } catch {
    return false;
  }
};

const getStoredClipToWorkarea = (): boolean => {
  try {
    const stored = localStorage.getItem(CLIP_TO_WORKAREA_KEY);
    return stored === null ? true : stored === "true";
  } catch {
    return true;
  }
};

const getStoredLabelWidth = (): number => {
  try {
    const stored = localStorage.getItem(LABEL_WIDTH_KEY);
    return stored ? clampLabelWidth(parseInt(stored, 10)) : 200;
  } catch {
    return 200;
  }
};

// Type for layer drag preview state - supports multiple layers for multi-select drag
export interface LayerDragPreviewLayer {
  layerUid: number | undefined;
  layerName: string;
  assetId: string | undefined;
  instancePath: string;
}

export interface LayerDragPreview {
  layers: LayerDragPreviewLayer[];
  delta: number; // Frame delta to apply while previewing the layer drag
}

export interface LayerEdgeDragPreview {
  layers: LayerDragPreviewLayer[];
  edge: "left" | "right";
  delta: number;
}

export interface KeyframeDragPreview {
  keyframeIds: string[];
  delta: number;
  anchorId: string;
  keyframeDeltas?: Record<string, number>;
}

export const TimelineModel = types
  .model("TimelineStore", {
    zoom: types.optional(types.number, 2), // pixels per frame
    scrollOffset: types.optional(types.number, 0), // horizontal scroll position
    panelHeight: types.optional(types.number, getStoredHeight()),
    labelWidth: types.optional(types.number, getStoredLabelWidth()),
    isCollapsed: types.optional(types.boolean, getStoredCollapsed()),
    expandedPrecomps: types.array(types.string), // keys of expanded precomp nodes
    keyframesCollapsed: types.optional(types.boolean, true), // Default: collapsed (keyframes hidden)
    keyframeExpandedLayers: types.array(types.string), // Layers with keyframes expanded (exceptions to global)
    clipToWorkarea: types.optional(types.boolean, getStoredClipToWorkarea()), // Clip layers to workarea bounds
    // Context for zoom percentage calculation
    workAreaFrames: types.optional(types.number, 0),
    viewportWidth: types.optional(types.number, 0),
    // Content extent for zoom limits (min zoom out to fit all layers)
    contentMinFrame: types.optional(types.number, 0),
    contentMaxFrame: types.optional(types.number, 0),
  })
  .volatile(() => ({
    // Transient state for layer body drag preview (not persisted)
    layerDragPreview: null as LayerDragPreview | null,
    layerEdgeDragPreview: null as LayerEdgeDragPreview | null,
    keyframeDragPreview: null as KeyframeDragPreview | null,
  }))
  .views((self) => ({
    // The zoom level that fits the work area exactly in the viewport
    get fitZoom() {
      if (self.workAreaFrames <= 0 || self.viewportWidth <= 0) return 2;
      return (self.viewportWidth - 40) / self.workAreaFrames; // 40px padding
    },
    // Minimum zoom to fit all content (including layers outside work area)
    get minZoom() {
      const contentSpan = self.contentMaxFrame - self.contentMinFrame;
      if (contentSpan <= 0 || self.viewportWidth <= 0) return 0.1;
      return (self.viewportWidth - 40) / contentSpan;
    },
    get zoomPercent() {
      // 100% = work area fits exactly in viewport
      const fit = this.fitZoom;
      if (fit <= 0) return 100;
      return Math.round((self.zoom / fit) * 100);
    },
    isPrecompExpanded(key: string) {
      return self.expandedPrecomps.includes(key);
    },
    isLayerKeyframesExpanded(nodeKey: string) {
      // If globally collapsed, check if this layer is an exception (expanded)
      // If globally expanded, check if this layer is an exception (collapsed)
      const isException = self.keyframeExpandedLayers.includes(nodeKey);
      return self.keyframesCollapsed ? isException : !isException;
    },
  }))
  .actions((self) => ({
    clampScrollOffsetToContent() {
      const maxScrollOffset = getMaxScrollOffset({
        clipToWorkarea: self.clipToWorkarea,
        contentMaxFrame: self.contentMaxFrame,
        viewportWidth: self.viewportWidth,
        workAreaFrames: self.workAreaFrames,
        zoom: self.zoom,
      });

      if (maxScrollOffset !== null && self.scrollOffset > maxScrollOffset) {
        self.scrollOffset = maxScrollOffset;
      }
    },
    setZoom(zoom: number) {
      // When clipToWorkarea is enabled, minimum zoom is fitZoom (100% - can only zoom in)
      // Otherwise, minimum zoom is minZoom (can zoom out to fit all content)
      const min = self.clipToWorkarea
        ? Math.max(0.1, self.fitZoom)
        : Math.max(0.1, self.minZoom);
      // Max zoom allows zooming in up to 1000% (10x from fitZoom) or at least 20 px/frame
      const max = Math.max(20, self.fitZoom * 10);
      self.zoom = Math.max(min, Math.min(max, zoom));
      this.clampScrollOffsetToContent();
    },
    setScrollOffset(offset: number) {
      self.scrollOffset = Math.max(0, offset);
      this.clampScrollOffsetToContent();
    },
    setWorkAreaContext(workAreaFrames: number, viewportWidth: number) {
      self.workAreaFrames = workAreaFrames;
      self.viewportWidth = viewportWidth;
      this.clampScrollOffsetToContent();
    },
    setContentExtent(minFrame: number, maxFrame: number) {
      self.contentMinFrame = minFrame;
      self.contentMaxFrame = maxFrame;
      this.clampScrollOffsetToContent();
    },
    setPanelHeight(height: number) {
      const clampedHeight = Math.max(100, Math.min(600, height));
      self.panelHeight = clampedHeight;
      localStorage.setItem(PANEL_HEIGHT_KEY, String(clampedHeight));
    },
    setLabelWidth(width: number) {
      const clampedWidth = clampLabelWidth(width);
      self.labelWidth = clampedWidth;
      localStorage.setItem(LABEL_WIDTH_KEY, String(clampedWidth));
    },
    setCollapsed(collapsed: boolean) {
      self.isCollapsed = collapsed;
      localStorage.setItem(PANEL_COLLAPSED_KEY, String(collapsed));
    },
    toggleCollapsed() {
      const newValue = !self.isCollapsed;
      self.isCollapsed = newValue;
      localStorage.setItem(PANEL_COLLAPSED_KEY, String(newValue));
    },
    togglePrecompExpansion(key: string) {
      const index = self.expandedPrecomps.indexOf(key);
      if (index >= 0) {
        self.expandedPrecomps.splice(index, 1);
      } else {
        self.expandedPrecomps.push(key);
      }
    },
    setPrecompExpanded(key: string, expanded: boolean) {
      const index = self.expandedPrecomps.indexOf(key);
      if (expanded && index < 0) {
        self.expandedPrecomps.push(key);
      } else if (!expanded && index >= 0) {
        self.expandedPrecomps.splice(index, 1);
      }
    },
    setExpandedPrecomps(keys: string[]) {
      self.expandedPrecomps.replace(keys);
    },
    toggleGlobalKeyframesCollapsed() {
      self.keyframesCollapsed = !self.keyframesCollapsed;
      // Clear all exceptions when toggling global state
      self.keyframeExpandedLayers.clear();
    },
    toggleLayerKeyframes(nodeKey: string) {
      const index = self.keyframeExpandedLayers.indexOf(nodeKey);
      if (index >= 0) {
        self.keyframeExpandedLayers.splice(index, 1);
      } else {
        self.keyframeExpandedLayers.push(nodeKey);
      }
    },
    toggleClipToWorkarea() {
      self.clipToWorkarea = !self.clipToWorkarea;
      localStorage.setItem(CLIP_TO_WORKAREA_KEY, String(self.clipToWorkarea));
      this.clampScrollOffsetToContent();
    },
    zoomIn() {
      this.setZoom(self.zoom * 1.2);
    },
    zoomOut() {
      this.setZoom(self.zoom / 1.2);
    },
    fitToView() {
      // Set zoom to fitZoom (100% = work area fits in viewport)
      if (self.fitZoom <= 0) return;
      this.setZoom(self.fitZoom);
      this.setScrollOffset(0);
    },
    reset() {
      self.zoom = 2;
      self.scrollOffset = 0;
    },
    setLayerDragPreview(preview: LayerDragPreview | null) {
      self.layerDragPreview = preview;
    },
    clearLayerDragPreview() {
      self.layerDragPreview = null;
    },
    setLayerEdgeDragPreview(preview: LayerEdgeDragPreview | null) {
      self.layerEdgeDragPreview = preview;
    },
    clearLayerEdgeDragPreview() {
      self.layerEdgeDragPreview = null;
    },
    setKeyframeDragPreview(preview: KeyframeDragPreview | null) {
      self.keyframeDragPreview = preview;
    },
    clearKeyframeDragPreview() {
      self.keyframeDragPreview = null;
    },
  }));

export type ITimelineModel = Instance<typeof TimelineModel>;
